import axios from 'axios';
import { cancelPushSubscription } from '../utils/pushCleanup';
import { redirectToLogin } from '../utils/loginRedirect';

/**
 * Token Manager
 * Handles proactive token refresh before expiration
 */

let refreshTimer = null;
let isRefreshing = false;

/**
 * Decode JWT token to get payload
 */
const decodeToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
};

/**
 * Get token expiration time in milliseconds
 */
const getTokenExpiration = (token) => {
  const payload = decodeToken(token);
  if (!payload || !payload.exp) {
    return null;
  }
  // exp is in seconds, convert to milliseconds
  return payload.exp * 1000;
};

/**
 * Calculate time until token refresh (refresh 1 minute before expiration)
 */
const getRefreshTimeout = (token) => {
  const expirationTime = getTokenExpiration(token);
  if (!expirationTime) {
    return null;
  }

  const now = Date.now();
  const timeUntilExpiration = expirationTime - now;

  // Refresh 1 minute before expiration
  const REFRESH_BUFFER = 1 * 60 * 1000; // 1 minute
  const refreshTime = Math.max(0, timeUntilExpiration - REFRESH_BUFFER);

  return refreshTime;
};

/**
 * Refresh the access token
 */
const refreshAccessToken = async () => {
  if (isRefreshing) {
    return;
  }

  isRefreshing = true;

  try {
    const refreshToken = localStorage.getItem('refresh_token');

    if (!refreshToken) {
      console.error('No refresh token available');
      stopTokenRefresh();
      await cancelPushSubscription();
      redirectToLogin();
      return;
    }

    const response = await axios.post(
      `${process.env.REACT_APP_AUTH_API_URL}/token/refresh/`,
      { refresh: refreshToken }
    );

    const { access } = response.data;

    if (access) {
      localStorage.setItem('access_token', access);
      console.log('Token refreshed successfully');

      // Schedule next refresh
      scheduleTokenRefresh(access);
    }
  } catch (error) {
    console.error('Token refresh failed:', error);

    // If refresh fails, clear tokens and redirect to login
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('collaborator');
    localStorage.removeItem('customer');
    localStorage.removeItem('businesses');

    stopTokenRefresh();
    await cancelPushSubscription();
    redirectToLogin();
  } finally {
    isRefreshing = false;
  }
};

/**
 * Schedule automatic token refresh
 */
export const scheduleTokenRefresh = (token) => {
  // Clear existing timer
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }

  if (!token) {
    token = localStorage.getItem('access_token');
  }

  if (!token) {
    return;
  }

  const timeout = getRefreshTimeout(token);

  if (timeout === null) {
    console.error('Could not determine token expiration');
    return;
  }

  // console.log(`Token refresh scheduled in ${Math.round(timeout / 1000)} seconds`);

  refreshTimer = setTimeout(() => {
    refreshAccessToken();
  }, timeout);
};

/**
 * Stop automatic token refresh
 */
export const stopTokenRefresh = () => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
};

/**
 * Initialize token refresh on app start
 */
export const initializeTokenRefresh = () => {
  const token = localStorage.getItem('access_token');

  if (token) {
    const expirationTime = getTokenExpiration(token);
    const now = Date.now();

    if (expirationTime && expirationTime > now) {
      // Token is still valid, schedule refresh
      scheduleTokenRefresh(token);
    } else {
      // Token is expired or invalid, try to refresh immediately
      console.log('Token expired, refreshing immediately');
      refreshAccessToken();
    }
  }
};

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', stopTokenRefresh);
}

// Mobile browsers freeze timers while the PWA is backgrounded, so a
// setTimeout scheduled before backgrounding can fire late (or the access
// token can expire before it fires at all). Recompute the refresh timing
// against the real clock whenever the app becomes visible again.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      scheduleTokenRefresh();
    }
  });
}
